import type { ResumeDocument } from "@/types/cv";

// Builder-friendly aliases for the nested ResumeDocument arrays. The builder UI
// collects a subset of ResumeDocument; AI polish (paid upsell) fills the rest
// (professional_summary, rewrite_title, experience_detail).
export type BuilderExperience = ResumeDocument["working_experience"][number];
export type BuilderEducation = ResumeDocument["education"][number];
export type BuilderLanguage =
  ResumeDocument["additional_information"]["languages"][number];

export const createEmptyExperience = (): BuilderExperience => ({
  title: "",
  rewrite_title: "",
  company: "",
  location: "",
  employment_type: "Full-time",
  start_date: null,
  end_date: null,
  is_current: false,
  experience_detail: "",
  points: [""],
});

export const createEmptyEducation = (): BuilderEducation => ({
  institution: "",
  degree: "",
  major: "",
  program_title: "",
  rewrite_program_title: "",
  start_date: null,
  end_date: null,
  gpa: "",
  points: [],
});

export const createEmptyResume = (): ResumeDocument => ({
  personal_identity: {
    name: "",
    email: "",
    phone_number: "",
    location: { city: "", country: "Indonesia", relocation_open: null },
    linkedin: "",
    additional_links: [],
  },
  professional_summary: "",
  working_experience: [createEmptyExperience()],
  organizations: [],
  education: [createEmptyEducation()],
  additional_information: {
    tools: [],
    technical_skills: [],
    soft_skills: [],
    languages: [],
    certificates_awards: [],
  },
});

// Strip empty bullet points and blank entries so the rendered docx and the
// grading payload stay clean. Non-destructive: returns a new document.
export const cleanResume = (resume: ResumeDocument): ResumeDocument => ({
  ...resume,
  working_experience: resume.working_experience
    .filter((entry) => entry.title.trim() || entry.company.trim())
    .map((entry) => ({
      ...entry,
      points: entry.points.map((point) => point.trim()).filter(Boolean),
    })),
  education: resume.education.filter(
    (entry) => entry.institution.trim() || entry.degree.trim(),
  ),
  additional_information: {
    ...resume.additional_information,
    tools: resume.additional_information.tools.filter(Boolean),
    technical_skills:
      resume.additional_information.technical_skills.filter(Boolean),
    soft_skills: resume.additional_information.soft_skills.filter(Boolean),
    languages: resume.additional_information.languages.filter(
      (lang) => lang.name.trim(),
    ),
  },
});

// Minimum bar to call the CV "ready": a name and at least one experience or
// education entry with substance.
export const isResumeReady = (resume: ResumeDocument): boolean => {
  if (!resume.personal_identity.name.trim()) return false;
  const hasExperience = resume.working_experience.some(
    (entry) => entry.title.trim() && entry.company.trim(),
  );
  const hasEducation = resume.education.some(
    (entry) => entry.institution.trim(),
  );
  return hasExperience || hasEducation;
};
